import {
    useEffect, useMemo, useRef, useState, useTransition,
} from "react";
import TicketItem from "./ticket-item";
import {
    ACTION_REGISTRY, ActionContext, ActionHandler, ActionType, EVENTS,
} from "./action-registry";
import { LoggingHandler } from "../logging/LoggingContext";
import useRouting, { Paths, RoutingProps } from "../Routing";

type SelectionStateDelegate = [TicketItem[], (value: TicketItem[]) => void];

type Handler = () => void | Promise<void>;
type HandlernStateDelegate = [Handler[], (value: Handler[]) => void];

export default class SelectionState {
    public static useSelectionState() {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const selectionState = useState<TicketItem[]>([]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handlerState = useState<Handler[]>([]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            const handlers = handlerState[0];
            while (handlers.isNotEmpty) {
                try {
                    const handler = handlers.pop();
                    if (handler) {
                        handler();
                    }
                } catch (err) {
                    LoggingHandler.instance.error({ err }, "Error calling after update effect");
                }
            }
        }, [handlerState[0]]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const state = useMemo(() => new SelectionState(selectionState, handlerState), []);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        state.setRouting(useRouting());
        return state;
    }

    private _isPaused = false;

    private selectionStack: TicketItem[][] = [];

    private selection: TicketItem[] = [];

    private _open?: TicketItem;

    private _routing?: RoutingProps;

    private constructor(
        private selectionState: SelectionStateDelegate,
        private handlerState: HandlernStateDelegate,
    ) {
        // IGNORE
    }

    public setSelection(tickets: TicketItem[]) {
        this.selection = tickets;
        this.selectionState[1](tickets);
    }

    public update(onCompleted?: (() => void | Promise<void>)) {
        this.handlerState[1](onCompleted ? [onCompleted] : []);
    }

    public get openItem() {
        return this._open;
    }

    public setRouting(routing: RoutingProps) {
        this._routing = routing;
    }

    public closeCurrent() {
        this._routing?.historyPushWithSearch(Paths.AGENT, this._routing?.getSearchParams(Paths.AGENT) as {search: string});
    }

    public setOpen(ticket?: TicketItem | undefined) {
        this._open = ticket;
    }

    public get current() {
        return this.selection.dedupe(row => row.id);
    }

    public createActionHandler(context: ActionContext) {
        return new ActionHandler(this.current, context, () => {
            this.update();
        });
    }

    public pushHistory() {
        this.selectionStack.push(this.current);
        this.setSelection([]);
    }

    public popHistory() {
        const items = this.selectionStack.pop();
        if (items) {
            this.setSelection(items ?? []);
        }
    }

    public clear() {
        try {
            this._isPaused = true;
            this.current.forEach(ticket => ticket.setIsChecked(false));
            this.setSelection([]);
        } finally {
            this._isPaused = false;
        }
    }

    public get isNotEmpty() {
        return this.selection?.isNotEmpty;
    }

    public get isEmpty() {
        return this.selection?.isEmpty;
    }

    public addSelection(...tickets: TicketItem[]) {
        this.setSelection([...this.current, ...tickets]);
    }

    public removeSelection(...tickets: TicketItem[]) {
        if (this._isPaused) {
            return;
        }
        const ticketSet = new Set(tickets.toIdList("id"));
        this.setSelection(this.current.filter(row => !ticketSet.has(row.id)));
    }

    public execute(
        action: ActionType,
        tickets?: TicketItem[],
        actionContext?: ActionContext,
        onCompleted?: () => void,
        onRollback?: () => void,
    ) {
        const items = tickets ?? this.current;
        EVENTS[action].getAll().forEach(event => {
            const actionHandler = new ActionHandler(items, actionContext ?? ActionContext.BULK_ACTION, onCompleted, onRollback);
            event.handleBulkAction(actionHandler);
        });
        this.update();
    }

    public getPossibleActions(actionContext: ActionContext, tickets?: TicketItem[]) {
        const items = tickets ?? this.current;
        return ACTION_REGISTRY.getAll().mapFilter(handler => {
            const button = handler.createBulkActionButton?.(items, actionContext);
            return button ? { handler, button: { canNotOverflow: false, ...button } } : undefined;
        }).sortBy(row => row.button.sortIndex ?? 0);
    }
}
